import { UTCDate } from '@date-fns/utc';

import {
  geographyBranchLabels,
  ricsAndCompanyBranchLabels,
  roleBranchLabels,
  skillsBranchLabels,
} from '../constants/branch-labels';
import {
  type DateRangeFilter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type ListFilter,
  type PrimarySelection,
  type TreeFilter,
} from '../types/filters';
import { enforceMaxSelections } from './primaryFilterUtils';

export const isValidId = (id: string) => /^[\d_]+$/.test(id);

export const validateParsedFilters = (
  parsedFilters: unknown,
  {
    defaultFilterMap,
    primaryFilterId,
    primaryFilterLimit,
  }: {
    defaultFilterMap: FilterSelectionsMap;
    primaryFilterId?: FilterId;
    primaryFilterLimit: number;
  }
) => {
  const newMap: FilterSelectionsMap = new Map(defaultFilterMap);

  // If the parsed object is invalid, bail early
  if (!parsedFilters || !Array.isArray(parsedFilters)) {
    console.error('Invalid filters object', parsedFilters);
    return newMap;
  }

  parsedFilters.forEach((parsedFilter) => {
    if (!Array.isArray(parsedFilter)) return;

    const [id, value] = parsedFilter;
    if (!id || !value) return;

    // -----------------------
    // Primary filter
    // -----------------------
    if (id === primaryFilterId) {
      const existingPrimaryFilter = newMap.get(id) as TreeFilter;
      const validatedPrimarySelections =
        value?.primarySelections?.filter((s: PrimarySelection) =>
          isValidId(s.id)
        ) || [];

      const validatedSelections =
        value?.selections?.filter((s: TreeFilter['selections'][number]) =>
          isValidId(s.id)
        ) || [];

      newMap.set(id, {
        ...existingPrimaryFilter,
        primarySelections: enforceMaxSelections(
          existingPrimaryFilter?.primarySelections || [],
          primaryFilterLimit
        ),
      });

      if (value.type !== FilterType.TREE) return;

      // If no valid selections, keep default filter map
      if (!validatedSelections.length) return;

      const valueWithValidatedActiveSelections = {
        ...value,
        primarySelections: enforceMaxSelections(
          validatedPrimarySelections,
          primaryFilterLimit
        ),
      } as TreeFilter;

      newMap.set(id, valueWithValidatedActiveSelections);
      return;
    }

    // -----------------------
    // Date range filter
    // -----------------------
    if (
      id === FilterId.DATE_RANGE &&
      value.type === FilterType.DATE_RANGE &&
      typeof value?.min === 'string' &&
      typeof value?.max === 'string'
    ) {
      const parsedDates: DateRangeFilter = {
        ...value,
        min: value.min ? new UTCDate(value.min) : null,
        max: value.max ? new UTCDate(value.max) : null,
      };
      newMap.set(id, parsedDates);
      return;
    }

    // -----------------------
    // Generic ID validation for all filter types with selections
    // -----------------------
    if (value?.type === FilterType.TREE && value?.selections) {
      // Tree filters (already handled above for specific types, but ensure all are covered)
      const validatedSelections = value.selections.filter(
        (s: TreeFilter['selections'][number]) => isValidId(s.id)
      );
      newMap.set(id, {
        ...value,
        selections: validatedSelections,
      });
      return;
    }

    if (value?.type === FilterType.LIST && value?.selections) {
      // List filters (GENDER, ETHNICITY, EDUCATION, SENIORITY, etc.)
      const validatedSelections = value.selections.filter(
        (s: ListFilter['selections'][number]) => isValidId(s.id)
      );
      newMap.set(id, {
        ...value,
        selections: validatedSelections,
      });
      return;
    }

    if (value?.type === FilterType.STATIC_LIST && value?.selections) {
      // Static list filters
      newMap.set(id, {
        ...value,
        selections: value.selections,
      });
      return;
    }

    newMap.set(id, value);
  });

  // ---------------------------------------------------------------------------
  // Reinstate branch labels that could be lost via URL state
  // ---------------------------------------------------------------------------
  const companyFilter = newMap.get(FilterId.COMPANY) as TreeFilter;
  if (companyFilter) {
    newMap.set(FilterId.COMPANY, {
      ...companyFilter,
      branches: ricsAndCompanyBranchLabels,
    });
  }

  const geographyFilter = newMap.get(FilterId.GEOGRAPHY) as TreeFilter;
  if (geographyFilter) {
    newMap.set(FilterId.GEOGRAPHY, {
      ...geographyFilter,
      branches: geographyBranchLabels,
    });
  }

  const roleFilter = newMap.get(FilterId.ROLE) as TreeFilter;
  if (roleFilter) {
    newMap.set(FilterId.ROLE, {
      ...roleFilter,
      branches: roleBranchLabels,
    });
  }

  const skillFilter = newMap.get(FilterId.SKILL) as TreeFilter;
  if (skillFilter) {
    newMap.set(FilterId.SKILL, {
      ...skillFilter,
      branches: skillsBranchLabels,
    });
  }

  return newMap;
};
